import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useAtom } from 'jotai';
import { SccTouchableOpacity } from '@/components/SccTouchableOpacity';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';
import { TextInput } from 'react-native';
import styled from 'styled-components/native';
import LeftArrowIcon from '@/assets/icon/ic_arrow_left.svg';
import ClearIcon from '@/assets/icon/ic_clear.svg';
import ListIcon from '@/assets/icon/ic_list.svg';
import MapIcon from '@/assets/icon/ic_map.svg';
import SearchIcon from '@/assets/icon/ic_search.svg';
import { color } from '@/constant/color.ts';
import { font } from '@/constant/font.ts';
import useNavigation from '@/navigation/useNavigation.ts';
import { draftKeywordAtom, searchQueryAtom, viewStateAtom } from '../../atoms';
export default function SearchInputText({ onTextUpdate, autoFocus, onBack, }) {
    var _a, _b, _c, _d;
    const [draftKeyword, setDraftKeyword] = useAtom(draftKeywordAtom);
    const [searchQuery, _] = useAtom(searchQueryAtom);
    const [viewState, setViewState] = useAtom(viewStateAtom);
    const navigation = useNavigation();
    const inputRef = useRef(null);
    // 뒤로가기처럼 코드에서 inputMode로 전환된 경우에도 검색 바를 탭한 것과 동일하게
    // 키보드가 올라와야 한다 (직접 탭해서 들어온 경우엔 이미 focus라 no-op).
    useEffect(() => {
        var _a;
        if (viewState.inputMode) {
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [viewState.inputMode]);
    const debounceSearch = useCallback(debounce((keyword) => {
        onTextUpdate(keyword, true);
    }, 300, { leading: false, trailing: true }), []);
    const isMap = viewState.type === 'map' && !viewState.inputMode;
    const onChange = (text) => {
        setDraftKeyword(text);
        debounceSearch(text);
    };
    // 확정 검색/지우기 뒤에 남은 미리보기 검색이 300ms 늦게 발동하면 방금 정한 검색 조건을 덮어쓴다.
    const onSubmit = () => {
        var _a;
        debounceSearch.cancel();
        onTextUpdate((_a = draftKeyword !== null && draftKeyword !== void 0 ? draftKeyword : searchQuery.text) !== null && _a !== void 0 ? _a : '', false);
    };
    const onClear = () => {
        debounceSearch.cancel();
        setDraftKeyword('');
        setViewState({ type: 'map', inputMode: false });
        onTextUpdate('', false);
        // route.params도 클리어해서 탭 unmount/remount 후 useEffect init이
        // 이전 keyword로 재검색 트리거하지 않게 한다.
        navigation.setParams({
            initKeyword: undefined,
            toMap: undefined,
            searchQuery: undefined,
            fromLookup: undefined,
            initSortOption: undefined,
        });
    };
    const isTextExists = viewState.inputMode
        ? ((_a = draftKeyword === null || draftKeyword === void 0 ? void 0 : draftKeyword.length) !== null && _a !== void 0 ? _a : 0) !== 0
        : ((_c = (_b = searchQuery.text) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) !== 0;
    return (_jsxs(Wrapper, { children: [!viewState.inputMode && (_jsx(SccTouchableOpacity, { elementName: "search_map_list_toggle_button", activeOpacity: 0.8, onPress: () => {
                    if (viewState.type === 'map') {
                        setViewState({ type: 'list', inputMode: false });
                    }
                    else {
                        setViewState({ type: 'map', inputMode: false });
                    }
                }, children: _jsx(MapListToggleButton, { children: isMap ? (_jsxs(_Fragment, { children: [_jsx(ListIcon, { width: 20, height: 20 }), _jsx(MapListToggleText, { children: "\uBAA9\uB85D" })] })) : (_jsxs(_Fragment, { children: [_jsx(MapIcon, { width: 20, height: 20 }), _jsx(MapListToggleText, { children: "\uC9C0\uB3C4" })] })) }) })), _jsxs(Container, { children: [_jsx(IconButton, { elementName: "search_back_button", onPress: () => {
                            if (!onBack()) {
                                navigation.goBack();
                            }
                        }, activeOpacity: 0.6, children: _jsx(LeftArrowIcon, { width: 16, height: 14, color: color.gray100 }) }), _jsx(Input, { ref: inputRef, style: { marginLeft: 8, marginRight: 12 }, placeholder: '장소, 주소 검색', placeholderTextColor: color.gray70, onChangeText: text => onChange(text), onFocus: () => {
                            var _a;
                            if (!viewState.inputMode || draftKeyword === null) {
                                setDraftKeyword((_a = searchQuery.text) !== null && _a !== void 0 ? _a : '');
                            }
                            setViewState(prev => (Object.assign(Object.assign({}, prev), { inputMode: true })));
                        }, value: viewState.inputMode
                            ? (draftKeyword !== null && draftKeyword !== void 0 ? draftKeyword : '')
                            : ((_d = searchQuery.text) !== null && _d !== void 0 ? _d : ''), returnKeyType: "search", onSubmitEditing: onSubmit, autoFocus: autoFocus }), _jsx(IconButton, { elementName: "search_clear_or_submit_button", onPress: () => {
                            isTextExists ? onClear() : onSubmit();
                        }, activeOpacity: 0.6, children: isTextExists ? (_jsx(ClearIcon, { width: 20, height: 20 })) : (_jsx(SearchIcon, { color: color.gray100, width: 14, height: 14 })) })] })] }));
}
const Wrapper = styled.View({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingLeft: 12,
    paddingRight: 12,
    gap: 12,
    height: 60,
});
const Input = styled(TextInput)({
    flex: 1,
    color: color.black,
    fontFamily: font.pretendardMedium,
    fontSize: 16,
    paddingVertical: 0,
});
const MapListToggleButton = styled.View `
  display: flex;
  border-radius: 8px;
  width: 26px;
  height: 38px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
`;
const MapListToggleText = styled.Text({
    color: color.black,
    fontFamily: font.pretendardRegular,
    fontSize: 12,
});
const Container = styled.View `
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 8px;
  background-color: ${color.gray10};
  padding-horizontal: 12px;
  height: 44px;
  padding-vertical: 0;
`;
const IconButton = styled(SccTouchableOpacity).attrs({
    hitSlop: 10,
}) `
  width: 24px;
  height: 24px;
  justify-content: center;
  align-items: center;
`;
